import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    TrendingUp,
    TrendingDown,
    Wallet,
    PieChart,
    Bell,
    Sparkles,
    X,
    Plus,
    Bot
} from 'lucide-react';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from 'recharts';

import { Button } from '../components/ui/button';
import DashboardLayout from '../components/DashboardLayout';
import { mockUser } from '../mockData';
import { supabase } from '../lib/supabase';
import { expenseAPI, portfolioAPI, reminderAPI, financialDataAPI, incomeAPI, recurringAPI } from '../services/apiServices';
import { calculateFinancialHealth } from '../lib/financialHealth';
import { useCachedApi } from '../hooks/useCachedApi';
import { useCache } from '../contexts/CacheContext';

// --- Custom CSS for Nebular Theme Animations & 3D Tilt ---
const customStyles = `
  .glass-card {
    background: rgba(15, 23, 42, 0.6);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.05);
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  /* 3D Tilt Card Base */
  .tilt-card-container {
    perspective: 1000px;
    transform-style: preserve-3d;
  }

  .tilt-card-inner {
    transition: transform 0.1s ease-out;
    transform-style: preserve-3d;
    background: rgba(30, 41, 59, 0.7); /* Darker inner card */
    border: 1px solid rgba(16, 185, 129, 0.1);
    box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.5);
  }

  .tilt-card-inner:hover {
    border-color: rgba(16, 185, 129, 0.4);
    box-shadow: 0 0 20px rgba(16, 185, 129, 0.2), inset 0 0 20px rgba(16, 185, 129, 0.05);
  }

  /* Spotlight Effect */
  .tilt-spotlight {
    background: radial-gradient(circle at var(--mouse-x) var(--mouse-y), rgba(16, 185, 129, 0.15), transparent 60%);
    z-index: 10;
    pointer-events: none;
  }

  /* Entrance Animations */
  @keyframes slideDownFade {
    from { opacity: 0; transform: translateY(-20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes slideUpFade {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes growWidth {
    from { width: 0; }
  }

  /* Flaming Emerald Border Animation */
  @keyframes flamingBorder {
    0% { border-color: rgba(16, 185, 129, 0.3); box-shadow: 0 0 10px rgba(16, 185, 129, 0.1), inset 0 0 5px rgba(16, 185, 129, 0.1); }
    50% { border-color: rgba(16, 185, 129, 0.8); box-shadow: 0 0 25px rgba(16, 185, 129, 0.4), inset 0 0 15px rgba(16, 185, 129, 0.2); }
    100% { border-color: rgba(16, 185, 129, 0.3); box-shadow: 0 0 10px rgba(16, 185, 129, 0.1), inset 0 0 5px rgba(16, 185, 129, 0.1); }
  }

  .flaming-emerald {
    animation: flamingBorder 2s ease-in-out infinite;
    background: #000000 !important;
  }
  
  .animate-slide-down { animation: slideDownFade 0.6s ease-out forwards; }
  .animate-slide-up { animation: slideUpFade 0.8s ease-out forwards; }
  .animate-grow { animation: growWidth 1.5s ease-out forwards; }

  .stagger-1 { animation-delay: 100ms; }
  .stagger-2 { animation-delay: 200ms; }
  .stagger-3 { animation-delay: 300ms; }
  .stagger-4 { animation-delay: 400ms; }
  .stagger-5 { animation-delay: 500ms; }
`;

const Dashboard = () => {
    const { invalidateCache } = useCache();
    const navigate = useNavigate();

    // Use cached API calls
    const { data: expenses = [], isFromCache: expensesFromCache } = useCachedApi(
        expenseAPI.getExpenses,
        'expenses',
        { onError: () => [] }
    );

    const { data: portfolio = [] } = useCachedApi(
        portfolioAPI.getPortfolio,
        'portfolio',
        { onError: () => [] }
    );

    const { data: newsData = [] } = useCachedApi(
        financialDataAPI.getNews,
        'news',
        { onError: () => [] }
    );

    const { data: incomes = [] } = useCachedApi(
        incomeAPI.getIncome,
        'income',
        { onError: () => [] }
    );

    const [userData, setUserData] = useState(mockUser);
    const [reminders, setReminders] = useState([]);
    const [showIncomeModal, setShowIncomeModal] = useState(false);


    const [newIncome, setNewIncome] = useState({
        source: '',
        amount: '',
        date: new Date().toISOString().split('T')[0]
    });

    const [showReminderPopup, setShowReminderPopup] = useState(false);
    const [upcomingReminders, setUpcomingReminders] = useState([]);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const { data: { user } } = await supabase.auth.getUser();

                if (!user) {
                    navigate('/login');
                    return;
                }

                // Fetch only user profile and reminders (other data is cached)
                const [remindersData, profileData] = await Promise.all([
                    reminderAPI.getReminders().catch(e => []),
                    supabase.from('profiles').select('*').eq('id', user.id).single()
                ]);

                const profile = profileData.data || {};
                const remindersList = remindersData || [];

                // Calculate Financial Health
                const healthResult = calculateFinancialHealth(
                    profile,
                    incomes || [],
                    expenses || [],
                    portfolio || [],
                    [],
                    remindersList
                );

                setUserData({
                    name: profile.name || user.email,
                    financialHealthScore: healthResult.score,
                    healthDetails: healthResult.details
                });

                // Reminders Logic - Refactored for Priority Thresholds
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const activeReminders = remindersList.filter(r => {
                    if (r.is_completed) return false;
                    const dueDate = new Date(r.due_date);
                    dueDate.setHours(0, 0, 0, 0);
                    const diffTime = dueDate.getTime() - today.getTime();
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                    // Thresholds: High (6), Medium (4), Low (3)
                    if (r.priority === 'High' && diffDays <= 6) return true;
                    if (r.priority === 'Medium' && diffDays <= 4) return true;
                    if (r.priority === 'Low' && diffDays <= 3) return true;
                    if (diffDays <= 0) return true; // Always show overdue/due today
                    return false;
                });
                setReminders(activeReminders);

                // Reminder popup logic - Trigger on new session or daily
                const lastShownDate = localStorage.getItem('lastReminderShownDate');
                const hasShownThisSession = sessionStorage.getItem('hasShownRemindersThisSession');
                const todayStr = today.toDateString();

                if ((lastShownDate !== todayStr || !hasShownThisSession) && activeReminders.length > 0) {
                    setShowReminderPopup(true);
                    sessionStorage.setItem('hasShownRemindersThisSession', 'true');
                }

                // Background price sync if stale (10 mins)
                const lastSync = sessionStorage.getItem('lastPortfolioUpdate');
                const TEN_MINS = 10 * 60 * 1000;
                if (portfolio?.length > 0 && (!lastSync || (Date.now() - Number(lastSync) > TEN_MINS))) {
                    console.log("[Dashboard] Portfolio stale, triggering background sync...");
                    // We don't await this to keep dashboard load fast
                    const syncPrices = async () => {
                        for (const item of portfolio) {
                            if (['Stocks', 'Mutual Fund', 'Crypto'].includes(item.asset_type) && item.ticker) {
                                try {
                                    let priceData;
                                    if (item.asset_type === 'Stocks') priceData = await financialDataAPI.getStockPrice(item.ticker);
                                    else if (item.asset_type === 'Mutual Fund') priceData = await financialDataAPI.getMFNAV(item.ticker);
                                    else if (item.asset_type === 'Crypto') priceData = await financialDataAPI.getCryptoPrice(item.ticker);

                                    if (priceData?.price) {
                                        await portfolioAPI.updatePricesBulk([{ id: item.id, current_price: priceData.price }]);
                                    }
                                } catch (e) { console.warn(`Sync failed for ${item.ticker}`); }
                            }
                        }
                        sessionStorage.setItem('lastPortfolioUpdate', Date.now().toString());
                        invalidateCache('portfolio'); // Refresh dashboard with new prices
                    };
                    syncPrices();
                }

            } catch (error) {
                console.error("Error loading dashboard data:", error);
            }
        };

        fetchDashboardData();
    }, [navigate, incomes, expenses, portfolio]);
    // Calculations
    const now = new Date();
    const currentMonth = now.getMonth(); // 0-indexed
    const currentYear = now.getFullYear();

    // 1. Process recurring items on mount
    useEffect(() => {
        const process = async () => {
            try {
                await recurringAPI.processDueRecurring();
                // If it added something, it will trigger a refetch via cache invalidation in recurringAPI or by manual trigger
                // Since processDueRecurring adds to incomes table, we might need a refresh.
            } catch (e) { console.error("Recurring check failed", e); }
        };
        process();
    }, []);

    // 2. Comprehensive Filtering
    const currentMonthIncomes = (incomes || []).filter(inc => {
        if (!inc.date) return false;
        const incDate = new Date(inc.date);
        return incDate.getMonth() === currentMonth && incDate.getFullYear() === currentYear;
    });

    const lastMonthIncomes = (incomes || []).filter(inc => {
        if (!inc.date) return false;
        const incDate = new Date(inc.date);
        const lastM = currentMonth === 0 ? 11 : currentMonth - 1;
        const lastY = currentMonth === 0 ? currentYear - 1 : currentYear;
        return incDate.getMonth() === lastM && incDate.getFullYear() === lastY;
    });

    const totalIncome = currentMonthIncomes.reduce((sum, inc) => sum + Number(inc.amount), 0);
    const lastMonthTotalIncome = lastMonthIncomes.reduce((sum, inc) => sum + Number(inc.amount), 0);

    const incomeTrend = lastMonthTotalIncome > 0
        ? (((totalIncome - lastMonthTotalIncome) / lastMonthTotalIncome) * 100).toFixed(1)
        : null;

    const currentMonthExpenses = (expenses || []).filter(exp => {
        if (!exp.date) return false;
        const expDate = new Date(exp.date);
        return expDate.getMonth() === currentMonth && expDate.getFullYear() === currentYear;
    });

    const totalExpenses = currentMonthExpenses.reduce((sum, exp) => sum + Number(exp.amount), 0);

    const totalInvested = (portfolio || []).reduce((sum, item) => sum + (Number(item.quantity) * Number(item.buy_price)), 0);
    const totalCurrentValue = (portfolio || []).reduce((sum, item) => {
        const price = item.current_price ? Number(item.current_price) : Number(item.buy_price);
        return sum + (Number(item.quantity) * price);
    }, 0);

    const profitLossValue = totalCurrentValue - totalInvested;
    const profitLossPercent = totalInvested > 0 ? ((profitLossValue / totalInvested) * 100).toFixed(2) : "0.00";

    // Category Breakdown Data (Pills)
    const categoryTotals = currentMonthExpenses.reduce((acc, curr) => {
        const cat = curr.category || 'Uncategorized';
        acc[cat] = (acc[cat] || 0) + Number(curr.amount);
        return acc;
    }, {});

    const categoryColors = {
        Food: 'linear-gradient(90deg, #F59E0B 0%, #D97706 100%)', // Orange
        Transport: 'linear-gradient(90deg, #3B82F6 0%, #2563EB 100%)', // Blue
        Shopping: 'linear-gradient(90deg, #8B5CF6 0%, #7C3AED 100%)', // Violet
        Bills: 'linear-gradient(90deg, #EF4444 0%, #DC2626 100%)', // Red
        Entertainment: 'linear-gradient(90deg, #EC4899 0%, #DB2777 100%)', // Pink
        Health: 'linear-gradient(90deg, #10B981 0%, #059669 100%)', // Emerald
        Education: 'linear-gradient(90deg, #6366F1 0%, #4F46E5 100%)', // Indigo
        Others: 'linear-gradient(90deg, #64748B 0%, #475569 100%)' // Slate
    };

    const categoryData = Object.keys(categoryTotals).map((cat) => ({
        category: cat,
        amount: categoryTotals[cat],
        percentage: totalExpenses > 0 ? ((categoryTotals[cat] / totalExpenses) * 100).toFixed(0) : 0,
        gradient: categoryColors[cat] || categoryColors.Others
    })).sort((a, b) => b.amount - a.amount);

    const handleAddIncome = async () => {
        if (!newIncome.source || !newIncome.amount) return;
        try {
            await incomeAPI.addIncome({
                source: newIncome.source,
                amount: Number(newIncome.amount),
                date: newIncome.date,
                type: 'income'
            });
            // Invalidate cache to trigger refetch
            invalidateCache('income');
            setNewIncome({ source: '', amount: '', date: new Date().toISOString().split('T')[0] });
            setShowIncomeModal(false);
        } catch (error) {
            console.error("Failed to add income", error);
        }
    };

    // --- CHART LOGIC (Area Chart) ---
    const processChartData = () => {
        const months = [];
        for (let i = 5; i >= 0; i--) {
            const d = new Date();
            d.setMonth(d.getMonth() - i);
            months.push({
                monthIndex: d.getMonth(),
                year: d.getFullYear(),
                label: d.toLocaleString('default', { month: 'short' }),
                Income: 0,
                Expense: 0
            });
        }

        (incomes || []).forEach(inc => {
            const d = new Date(inc.date);
            const mIdx = d.getMonth(); const y = d.getFullYear();
            const monthData = months.find(m => m.monthIndex === mIdx && m.year === y);
            if (monthData) monthData.Income += Number(inc.amount);
        });

        (expenses || []).forEach(exp => {
            const d = new Date(exp.date);
            const mIdx = d.getMonth(); const y = d.getFullYear();
            const monthData = months.find(m => m.monthIndex === mIdx && m.year === y);
            if (monthData) monthData.Expense += Number(exp.amount);
        });
        return months;
    };

    const chartData = processChartData();

    return (
        <>
            <style>{customStyles}</style>

            {/* Premium Reminder Flashcard - Root Level to avoid blocking */}
            {showReminderPopup && (
                <div
                    className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md animate-in fade-in duration-300"
                    onClick={() => {
                        setShowReminderPopup(false);
                        localStorage.setItem('lastReminderShownDate', new Date().toDateString());
                    }}
                >
                    <div
                        className="flaming-emerald relative overflow-hidden rounded-[2.5rem] border-2 p-8 shadow-2xl w-full max-w-md animate-in zoom-in-95 duration-300 pointer-events-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-emerald-500/10 to-transparent opacity-50"></div>

                        <div className="relative z-[100]">
                            <div className="flex justify-between items-start mb-8">
                                <div className="p-3 bg-emerald-500/10 rounded-2xl border border-emerald-500/20 text-emerald-400">
                                    <Bell className="animate-bounce" size={28} />
                                </div>
                                <button
                                    onClick={() => {
                                        setShowReminderPopup(false);
                                        localStorage.setItem('lastReminderShownDate', new Date().toDateString());
                                    }}
                                    className="p-2 hover:bg-white/10 rounded-full text-slate-400 hover:text-white transition-all cursor-pointer bg-white/10 active:scale-90 border border-white/5"
                                >
                                    <X size={24} />
                                </button>
                            </div>

                            <div className="space-y-2 mb-8">
                                <h2 className="text-3xl font-black text-white tracking-tighter uppercase font-jakarta">Reminders <span className="text-emerald-500">Pending</span></h2>
                                <p className="text-slate-400 font-medium">You have {reminders.length} upcoming tasks that need your attention.</p>
                            </div>

                            <div className="space-y-4 mb-10 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                                {reminders.slice(0, 3).map((r) => (
                                    <div key={r.id} className="flex items-center justify-between p-4 rounded-2xl bg-zinc-900 border border-white/10 hover:border-emerald-500/30 transition-all font-jakarta">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <p className="text-white font-bold text-[14px] tracking-tight">{r.title}</p>
                                                <span className={`text-[11px] font-black px-1.5 py-0.5 rounded uppercase ${r.priority === 'High' ? 'bg-red-500 text-black' :
                                                    r.priority === 'Medium' ? 'bg-orange-500 text-black' :
                                                        'bg-yellow-500 text-black'
                                                    }`}>
                                                    {r.priority}
                                                </span>
                                            </div>
                                            <p className="text-[12px] font-black uppercase tracking-widest text-slate-400">{new Date(r.due_date).toLocaleDateString()}</p>
                                        </div>
                                        <p className="text-white font-black text-[16px] ml-4">₹{Number(r.amount).toLocaleString('en-IN')}</p>
                                    </div>
                                ))}
                                {reminders.length > 3 && (
                                    <p className="text-center text-[12px] font-black text-slate-400 tracking-[0.3em] py-2 uppercase">+{reminders.length - 3} More Reminders</p>
                                )}
                            </div>

                            <button
                                onClick={() => {
                                    setShowReminderPopup(false);
                                    localStorage.setItem('lastReminderShownDate', new Date().toDateString());
                                    navigate('/reminders');
                                }}
                                className="w-full py-4 rounded-2xl bg-white text-black font-black text-xs uppercase tracking-[0.2em] shadow-xl hover:bg-slate-200 active:scale-[0.98] transition-all duration-300 font-jakarta cursor-pointer"
                            >
                                View All Reminders
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <DashboardLayout>
                {showIncomeModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in">
                        <div className="glass-card rounded-2xl max-w-sm w-full p-6 border border-emerald-500/30">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-xl font-bold text-white">Add Income</h3>
                                <button onClick={() => setShowIncomeModal(false)}><X size={20} className="text-gray-400" /></button>
                            </div>
                            <div className="space-y-4">
                                <input type="date" className="w-full p-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white" value={newIncome.date} onChange={(e) => setNewIncome({ ...newIncome, date: e.target.value })} />
                                <input type="text" placeholder="Source" className="w-full p-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white" value={newIncome.source} onChange={(e) => setNewIncome({ ...newIncome, source: e.target.value })} />
                                <input type="number" placeholder="Amount" className="w-full p-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white" value={newIncome.amount} onChange={(e) => setNewIncome({ ...newIncome, amount: e.target.value })} />
                                <Button onClick={handleAddIncome} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-xl mt-2">Add Income</Button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="p-6 md:p-8">
                    {/* Header Title - Updated to Match Theme - Strict Parity */}
                    <div className="mb-8 flex flex-col items-start animate-in fade-in slide-in-from-left-6 duration-1000 w-full">
                        <div className="mb-3 flex items-center gap-2 bg-orange-500/10 px-2 py-0.5 rounded-full border border-orange-500/20">
                            <Sparkles size={10} className="text-orange-400" />
                            <span className="text-[8px] font-black uppercase tracking-[0.3em] text-orange-400">FinAssure Executive Overview</span>
                        </div>
                        <h1 className="text-xl md:text-3xl font-black tracking-tighter text-white uppercase leading-none mb-3">
                            EXECUTIVE <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-500">DASHBOARD</span>
                        </h1>
                        <p className="text-slate-400 text-[13px] max-w-2xl font-medium leading-relaxed">
                            Welcome back, <span className="text-slate-100 font-bold">{userData.name || 'User'}</span>. Here is your comprehensive financial ecosystem overview.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        <StatCard
                            title="Total Income"
                            value={`₹${totalIncome.toLocaleString('en-IN')}`}
                            icon={<TrendingUp />}
                            theme="green"
                            trend={incomeTrend ? `${incomeTrend > 0 ? '+' : ''}${incomeTrend}%` : "Stable"}
                            trendUp={incomeTrend ? Number(incomeTrend) >= 0 : true}
                            onAdd={() => setShowIncomeModal(true)}
                            delay="stagger-1"
                        />
                        <StatCard
                            title="Total Expenses"
                            value={`₹${totalExpenses.toLocaleString('en-IN')}`}
                            icon={<TrendingDown />}
                            theme="orange"
                            trend={totalExpenses > 0 ? "Active" : "Stable"}
                            trendUp={totalExpenses === 0}
                            delay="stagger-2"
                        />
                        <StatCard
                            title="Networth"
                            value={`₹${totalCurrentValue.toLocaleString('en-IN')}`}
                            icon={<PieChart />}
                            theme="blue"
                            trend={`${profitLossPercent}%`}
                            trendUp={Number(profitLossPercent) >= 0}
                            delay="stagger-3"
                        />
                        <StatCard
                            title="Financial Health"
                            value={`${userData.financialHealthScore}/100`}
                            icon={<Sparkles />}
                            theme="purple"
                            trend={userData.financialHealthScore > 80 ? "Excellent" : "Good"}
                            trendUp={true}
                            delay="stagger-4"
                            details={userData.healthDetails}
                        />
                    </div>

                    <TiltCard glowColor="#EF4444" className="mb-8 p-4 md:p-6 bg-black border border-white/10 rounded-2xl animate-slide-up shadow-2xl">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                            <div>
                                <h2 className="text-xl font-bold text-white">Income vs Expense Trends</h2>
                                <p className="text-sm text-slate-400">6-Month financial overview</p>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <span className="w-3 h-3 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.5)]"></span>
                                    <span className="text-sm text-slate-300">Income</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="w-3 h-3 rounded-full bg-rose-500 shadow-[0_0_10px_rgba(244,63,94,0.5)]"></span>
                                    <span className="text-sm text-slate-300">Expense</span>
                                </div>
                            </div>
                        </div>

                        <div className="h-[250px] md:h-[350px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                                    <defs>
                                        <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                                            <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                                        </linearGradient>
                                        <linearGradient id="colorExpense" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3} />
                                            <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#334155" opacity={0.1} />
                                    <XAxis
                                        dataKey="label"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#94A3B8', fontSize: 12, fontWeight: 500 }}
                                        dy={10}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#94A3B8', fontSize: 12 }}
                                        tickFormatter={(value) => `₹${value / 1000}k`}
                                    />
                                    <Tooltip
                                        cursor={{ stroke: '#64748B', strokeWidth: 1, strokeDasharray: '3 3' }}
                                        contentStyle={{
                                            borderRadius: '12px',
                                            border: '1px solid rgba(100, 116, 139, 0.2)',
                                            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.6)',
                                            padding: '12px 16px',
                                            backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                            backdropFilter: 'blur(16px)',
                                            color: '#fff'
                                        }}
                                        itemStyle={{ fontSize: '13px', fontWeight: 600, padding: '2px 0' }}
                                    />
                                    <Area type="monotone" dataKey="Income" stroke="#10B981" strokeWidth={3} fillOpacity={1} fill="url(#colorIncome)" animationDuration={1500} />
                                    <Area type="monotone" dataKey="Expense" stroke="#EF4444" strokeWidth={3} fillOpacity={1} fill="url(#colorExpense)" animationDuration={1500} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </TiltCard>

                    <div className="grid lg:grid-cols-3 gap-6">
                        <TiltCard glowColor="#10B981" className="lg:col-span-2 p-6 bg-black border border-white/10 rounded-2xl animate-slide-up shadow-2xl">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-xl font-bold text-white">Expense Breakdown</h2>
                                <Link to="/expenses" className="text-sm text-slate-400 hover:text-white transition-colors">View All</Link>
                            </div>
                            <div className="space-y-5">
                                {categoryData.length > 0 ? categoryData.slice(0, 5).map((cat) => (
                                    <div key={cat.category} className="group">
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="text-sm font-medium text-slate-300 group-hover:text-emerald-400 transition-colors">{cat.category}</span>
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-bold text-slate-500">({cat.percentage}%)</span>
                                                <span className="text-sm font-bold text-white">₹{cat.amount.toLocaleString('en-IN')}</span>
                                            </div>
                                        </div>
                                        <div className="w-full bg-[#1E293B] rounded-full h-4 overflow-hidden border border-white/5">
                                            <div
                                                className="h-full rounded-full animate-grow shadow-lg relative"
                                                style={{
                                                    width: `${cat.percentage}%`,
                                                    background: cat.gradient,
                                                    boxShadow: `0 0 12px ${cat.gradient.match(/#[0-9a-fA-F]{6}/)?.[0] || 'rgba(0,0,0,0.1)'}`
                                                }}
                                            >
                                            </div>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="text-slate-500 text-center py-8">No expenses yet. Start tracking!</div>
                                )}
                            </div>
                        </TiltCard>

                        <div className="flex flex-col gap-6">
                            <TiltCard glowColor="#3B82F6" className="p-6 bg-black border border-white/10 rounded-2xl animate-slide-up delay-200 shadow-2xl">
                                <h2 className="text-xl font-bold text-white mb-6">Quick Actions</h2>
                                <div className="flex flex-col gap-3">
                                    {[
                                        { label: 'Add Expense', icon: Wallet, link: '/expenses', color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', hover: 'hover:bg-emerald-500/20' },
                                        { label: 'Portfolio', icon: PieChart, link: '/portfolio', color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20', hover: 'hover:bg-blue-500/20' },
                                        { label: 'Finance Chatbot', icon: Bot, link: '/ai-tools', color: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/20', hover: 'hover:bg-rose-500/20' },
                                        { label: 'Investments', icon: TrendingUp, link: '/investments', color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', hover: 'hover:bg-cyan-500/20' },
                                    ].map((action, i) => (
                                        <Link key={i} to={action.link} className="block">
                                            <Button className={`w-full h-12 justify-start ${action.bg} ${action.hover} border ${action.border} text-slate-200 hover:text-white transition-all hover:scale-[1.02] hover:shadow-lg rounded-xl flex items-center px-4`} variant="outline">
                                                <div className={`p-2 rounded-lg mr-3 bg-black/20`}>
                                                    <action.icon className={action.color} size={18} />
                                                </div>
                                                <span className="font-semibold text-sm">{action.label}</span>
                                            </Button>
                                        </Link>
                                    ))}
                                </div>
                            </TiltCard>
                        </div>
                    </div>
                </div>
            </DashboardLayout >
        </>
    );
};

// --- 3D Tilt Card Component ---
const TiltCard = ({ children, className, glowColor, ...props }) => {
    const cardRef = useRef(null);
    const [style, setStyle] = useState({});

    const handleMouseMove = (e) => {
        if (!cardRef.current) return;
        const card = cardRef.current;
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const rotateX = ((y - centerY) / centerY) * -10; // Max 10deg rotation
        const rotateY = ((x - centerX) / centerX) * 10;

        setStyle({
            transform: `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`,
            transition: 'transform 0.1s ease-out',
            boxShadow: `0 0 20px ${glowColor}40, inset 0 0 10px ${glowColor}10` // Dynamic glow
        });
    };

    const handleMouseLeave = () => {
        setStyle({
            transform: `perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)`,
            transition: 'transform 0.5s ease-out',
            boxShadow: `0 0 0px ${glowColor}00` // Reset glow
        });
    };

    return (
        <div
            ref={cardRef}
            className={className}
            onMouseMove={handleMouseMove}
            onMouseLeave={(e) => {
                handleMouseLeave();
                if (props.onMouseLeave) props.onMouseLeave(e);
            }}
            {...props}
            style={style}
        >
            {children}
        </div>
    );
};

// --- Updated Flashcard Component ---
const StatCard = ({ title, value, icon, theme, trend, trendUp, delay, onAdd, details }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [animatedValue, setAnimatedValue] = useState(0);

    // Odometer Animation for Health Score
    useEffect(() => {
        if (theme === 'purple' && typeof value === 'string') {
            const target = parseInt(value);
            if (isNaN(target)) return;
            let start = 0;
            const duration = 1500;
            const startTime = performance.now();
            const animate = (currentTime) => {
                const elapsed = currentTime - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const easeOutExpo = 1 - Math.pow(2, -10 * progress);
                setAnimatedValue(Math.floor(start + (target - start) * easeOutExpo));
                if (progress < 1) requestAnimationFrame(animate);
            };
            requestAnimationFrame(animate);
        } else {
            setAnimatedValue(value);
        }
    }, [value, theme]);

    // Definitive Color Mapping for "Single Color per Box"
    const themeStyles = {
        green: {
            borderColor: '#10B981', // Emerald 500
            glowColor: '#10B981',
            iconColor: 'text-emerald-400',
            bgClass: 'bg-emerald-950/30', // Tinted Green
            bgGradient: 'from-emerald-500/10 to-transparent',
            pillClass: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50'
        },
        orange: {
            borderColor: '#F97316', // Orange 500
            glowColor: '#F97316',
            iconColor: 'text-orange-400',
            bgClass: 'bg-orange-950/30', // Tinted Orange
            bgGradient: 'from-orange-500/10 to-transparent',
            pillClass: 'bg-orange-500/20 text-orange-400 border-orange-500/50'
        },
        red: {
            borderColor: '#EF4444', // Red 500
            glowColor: '#EF4444',
            iconColor: 'text-rose-400',
            bgClass: 'bg-rose-950/30', // Tinted Red
            bgGradient: 'from-rose-500/10 to-transparent',
            pillClass: 'bg-rose-500/20 text-rose-400 border-rose-500/50'
        },
        blue: {
            borderColor: '#3B82F6', // Blue 500
            glowColor: '#3B82F6',
            iconColor: 'text-blue-400',
            bgClass: 'bg-blue-950/30', // Tinted Blue
            bgGradient: 'from-blue-500/10 to-transparent',
            pillClass: 'bg-blue-500/20 text-blue-400 border-blue-500/50'
        },
        purple: {
            borderColor: '#A855F7', // Purple 500
            glowColor: '#A855F7',
            iconColor: 'text-purple-400',
            bgClass: 'bg-purple-950/30', // Tinted Purple
            bgGradient: 'from-purple-500/10 to-transparent',
            pillClass: 'bg-purple-500/20 text-purple-400 border-purple-500/50'
        }
    };

    const style = themeStyles[theme] || themeStyles.green;

    return (
        <TiltCard
            glowColor={style.glowColor}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={`
                relative p-5 rounded-2xl ${style.bgClass} 
                backdrop-blur-xl
                flex flex-col justify-between overflow-hidden
                ${delay} animate-slide-down shadow-lg
                cursor-default min-h-[160px]
            `}
        >
            {/* Real-time Calculation Floating Window - manifested on cursor hover */}
            {theme === 'purple' && details && (
                <div className={`
                    absolute inset-0 z-50 p-5
                    bg-black/95 backdrop-blur-3xl
                    transition-all duration-500 ease-in-out
                    flex flex-col justify-center
                    ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8 pointer-events-none'}
                `}>
                    <div className="flex items-center gap-2 mb-3">
                        <div className="h-2 w-2 rounded-full bg-purple-500 animate-pulse shadow-[0_0_10px_#A855F7]" />
                        <p className="text-[9px] font-black text-purple-400 uppercase tracking-[0.2em] italic">Health Insight Engine</p>
                    </div>
                    <div className="space-y-2">
                        {details.map((detail, idx) => (
                            <div
                                key={idx}
                                className="flex items-start gap-2 animate-in fade-in slide-in-from-left duration-500"
                                style={{ animationDelay: `${idx * 80}ms` }}
                            >
                                <div className="mt-1.5 w-1 h-1 rounded-full bg-purple-500/50" />
                                <p className="text-[8px] font-bold text-slate-300 leading-tight uppercase tracking-tight italic">{detail}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className={`absolute -right-10 -top-10 w-32 h-32 rounded-full bg-gradient-to-br ${style.bgGradient} blur-[60px] opacity-10 pointer-events-none`}></div>

            <div className="relative z-10 w-full flex flex-col h-full">
                <div className="flex justify-between items-start mb-4">
                    <div className={`w-9 h-9 rounded-xl bg-black/40 border border-white/5 flex items-center justify-center ${style.iconColor} shadow-inner`}>
                        {React.cloneElement(icon, { size: 18 })}
                    </div>
                    {trend && (
                        <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full border ${style.pillClass}`}>
                            {trend}
                        </span>
                    )}
                </div>

                <div className="mt-auto">
                    <p className="text-slate-400 text-[9px] font-black uppercase tracking-[0.2em] mb-1">{title}</p>
                    <div className="flex items-baseline justify-between">
                        <h3 className={`font-black text-white tracking-tighter ${theme === 'purple' ? 'text-3xl' : 'text-2xl'}`}>
                            {theme === 'purple' ? (
                                <span className="flex items-baseline gap-1">
                                    {animatedValue}
                                    <span className="text-lg opacity-40">/100</span>
                                </span>
                            ) : value}
                        </h3>

                        {theme === 'green' ? (
                            <Link to="/income" className={`h-6 px-3 rounded-full border border-white/10 flex items-center justify-center ${style.iconColor} hover:bg-white/10 transition-all active:scale-95 text-[8px] uppercase font-black tracking-widest`}>
                                History
                            </Link>
                        ) : onAdd && (
                            <button
                                onClick={(e) => { e.stopPropagation(); onAdd(); }}
                                className={`w-6 h-6 rounded-full border border-white/10 flex items-center justify-center ${style.iconColor} hover:bg-white/10 transition-all active:scale-95`}
                            >
                                <Plus size={12} />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </TiltCard>
    );
};



export default Dashboard;
